import React from 'react';
import {PropTypes} from 'prop-types';
import './Viewport.less';
import {Location, ReactInstance} from 'virtual-tour-viewer/src/modules/React360Proxy';
import ObservableStateModule from 'virtual-tour-viewer/src/modules/ObservableStateModule';
import NativeXHRModule from 'virtual-tour-viewer/src/modules/NativeXHR/NativeXHRModule';
import NativeFileReaderUtilitiesModule from 'virtual-tour-viewer/src/modules/NativeFileReaderUtilitiesModule';
import UIOverlayModule from '../../../../modules/UIOverlayModule.react';
import CameraDirector from '../../../../ControlsDirectors/CameraDirector';
import RaycasterDirector from '../../../../ControlsDirectors/RaycasterDirector';
import InputChannelDirector from '../../../../ControlsDirectors/InputChannelDirector';
import KeyboardPanCameraController from '../../../../CameraControllers/KeyboardPanCameraController';
import PointsOfInterestCameraController from '../../../../CameraControllers/PointsOfInterestCameraController';
import OverlayActions from '../../../../data/Overlay/OverlayActions';
import TourActionTypes from 'virtual-tour-viewer/src/data/TourActionTypes';
import EdapEventMethods from 'virtual-tour-viewer/src/modules/EdapEventMethods';
import {getOptimalHeading} from 'virtual-tour-viewer/src/utils/hotSpotDataHelper';
import LogPropTypes from '../../../../Contexts/Logging/PropTypes';
import withLog from '../../hoc/WithLog.react';

class Viewport extends React.Component {
    static propTypes = {
        bundle: PropTypes.string.isRequired,
        options: PropTypes.object, // eslint-disable-line react/forbid-prop-types
        params: PropTypes.shape({
            needToAdjustCamera: PropTypes.bool,
        }),
        // TODO: Define the shape of tourData in the viewerPackage such that it can be reused here.
        // eslint-disable-next-line react/forbid-prop-types
        tourData: PropTypes.object.isRequired,
        initialImage: PropTypes.object, // eslint-disable-line react/forbid-prop-types
        log: PropTypes.shape(LogPropTypes),
        navigation: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    };

    constructor(props) {
        /**
         * TODO: Need to do the configuration when receiving props
         * and move r360 to state. There's a shit-ton more clean up to do here,
         * but at least our react-360 interactions are now encapsulated within
         * this component.
         *
         * We should not be directly defining the slew of callbacks that follow
         * here, but in the interest of time I (jmckinney) intend to continue
         * this clean up later.
         */
        super(props);
        this.ref = React.createRef();
        this.viewport = document.createElement('div');
        this.viewport.className = 'viewport';
        const observableStateModule = new ObservableStateModule();
        const XHRModule = new NativeXHRModule();
        const FileReaderModule = new NativeFileReaderUtilitiesModule();
        const fov = 90;
        const log = props.log;

        this.r360 = new ReactInstance(props.bundle, this.viewport, {
            // Add custom options here
            fullScreen: false,
            frame: () => {},
            nativeModules: [observableStateModule, XHRModule, FileReaderModule],
            fov,
            ...props.options,
        });

        XHRModule.setContext(this.r360.runtime.context);
        FileReaderModule.setContext(this.r360.runtime.context);

        const cameraDirector = new CameraDirector(this.r360, fov);
        const raycasterDirector = new RaycasterDirector(this.r360, fov);
        // eslint-disable-next-line no-unused-vars
        const inputChannelDirector = new InputChannelDirector(this.r360);

        let optimalHeadings = new Map();

        cameraDirector.onLeadChanged((lead) => {
            if (
                lead instanceof KeyboardPanCameraController ||
                lead instanceof PointsOfInterestCameraController
            ) {
                raycasterDirector.useCrosshair();
            } else {
                raycasterDirector.usePointer();
            }
        });

        OverlayActions.setContext(this.r360);

        observableStateModule.addObserver(new UIOverlayModule());

        observableStateModule.addObserver(
            {
                update: (eventProps) => {
                    log.info(`TourAction.${TourActionTypes.SELECT_GROUP}`, eventProps);
                },
            },
            TourActionTypes.SELECT_GROUP
        );

        observableStateModule.addObserver(
            {
                update: (eventProps) => {
                    log.info(`TourAction.${TourActionTypes.SPHERE_LOADED}`, eventProps);
                    cameraDirector.setFloorId(eventProps.state.groupId);
                    OverlayActions.showLoadingOverlay(false);
                    OverlayActions.clearNotifications();
                    if (eventProps.state.navigationMethod !== EdapEventMethods.history) {
                        history.pushState(null, null, `#${eventProps.state.currentSphereId}`);
                    }
                    window.parent.postMessage(
                        `SPHERE_LOADED: ${eventProps.state.currentSphereId}`,
                        '*'
                    );
                },
            },
            TourActionTypes.SPHERE_LOADED
        );

        const reloadCallback = () => {
            window.location.reload();
        };

        observableStateModule.addObserver(
            {
                update: (eventProps) => {
                    OverlayActions.showLoadingOverlay(false);
                    OverlayActions.addNotification(
                        'notifications.serviceerror.title',
                        'notifications.serviceerror.prompt',
                        [
                            {
                                labelKey: 'notifications.serviceerror.reload',
                                callback: reloadCallback,
                            },
                        ]
                    );
                },
            },
            TourActionTypes.SPHERE_FAILED
        );

        observableStateModule.addObserver(
            {
                update: (eventProps) => {
                    log.info(`TourAction.${TourActionTypes.LOAD_SPHERE}`, eventProps);
                    cameraDirector.setTourId(eventProps.state.tourId);
                    cameraDirector.setSphereId(eventProps.state.currentSphereId);
                    OverlayActions.scheduleLoadingOverlay(true);
                },
            },
            TourActionTypes.LOAD_SPHERE
        );

        observableStateModule.addObserver(
            {
                update: (eventProps) => {
                    cameraDirector.setPointsOfInterest(
                        eventProps.state.hotspotData.map((hotspot) => {
                            return hotspot.coordinates;
                        })
                    );
                },
            },
            TourActionTypes.SET_HOTSPOTS
        );

        observableStateModule.addObserver(
            {
                update: (eventProps) => {
                    if (
                        eventProps.state.navigationMethod === EdapEventMethods.initialLoad ||
                        eventProps.state.navigationMethod === EdapEventMethods.floorMenuClick ||
                        eventProps.state.navigationMethod === EdapEventMethods.spaceClick
                    ) {
                        let hotspotData = eventProps.state.hotspotData;
                        if (hotspotData.length && props.params.needToAdjustCamera) {
                            // check map for already calculated headings
                            if (
                                optimalHeadings.has(eventProps.state.currentSphereId) &&
                                eventProps.state.navigationMethod !== EdapEventMethods.spaceClick
                            ) {
                                cameraDirector.lookAt(
                                    optimalHeadings.get(eventProps.state.currentSphereId)
                                );
                            } else {
                                let result = getOptimalHeading(
                                    hotspotData,
                                    props.navigation,
                                    eventProps.state.currentSphereId,
                                    eventProps.state.navigationMethod
                                );
                                optimalHeadings.set(eventProps.state.currentSphereId, result);
                                cameraDirector.lookAt(result);
                            }
                        }
                    }
                },
            },
            TourActionTypes.SET_HOTSPOTS
        );

        observableStateModule.addObserver(
            {
                update: (eventProps) => {
                    log.info(`TourAction.${TourActionTypes.HOTSPOT_DISPLAYED}`, eventProps);
                },
            },
            TourActionTypes.HOTSPOT_DISPLAYED
        );
        this.r360.compositor._canvas.tabIndex = 0;

        // Hide compass
        this.r360.overlay.hide();

        let initialProps = {
            ...props.params,
            initialImage: props.initialImage,
            tourData: props.tourData,
        };

        this.r360.renderToLocation(
            this.r360.createRoot('VirtualTourViewer', initialProps),
            new Location([0, 0, 0])
        );

        this.r360.renderToLocation(
            this.r360.createRoot('VirtualTourUIProxy', initialProps),
            new Location([0, 0, 0])
        );
    }

    componentDidMount(): void {
        this._updateViewport();
        window.addEventListener('resize', this._updateViewport);
    }

    componentDidUpdate(): void {
        this._updateViewport();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this._updateViewport);
    }

    _updateViewport = () => {
        if (this.viewport && this.r360) {
            this.ref.current.appendChild(this.viewport);
            this.r360.resize(this.ref.current.clientWidth, this.ref.current.clientHeight);
        }
    };

    render() {
        return <div className="viewportParent" ref={this.ref} />;
    }
}

export default withLog(Viewport);
